/* ==========================================================================
   booking-form.js — build item 3b
   --------------------------------------------------------------------------
   Owned by sections/contact-form.liquid. Loaded deferred by that section, and
   ONLY when form_mode is `booking` or `embed` — the original contact form has no
   JavaScript half and must not gain a request.

   WHAT THIS FILE IS FOR

   The form is a real Shopify {% form 'contact' %}. Everything a browser can do
   for free is left to the browser:

     · required / type=email / type=tel / min / max are NATIVE attributes, so the
       form is validated, announced and bubble-messaged by the platform. The form
       carries no `novalidate`, deliberately — hand-rolled validation is how a
       theme ends up with error copy that no screen reader reaches.
     · Date bounds are computed in Liquid, not here, so they are correct before
       this script parses.
     · The three time selects render, label and focus correctly with JS off.

   So this file only does the five things markup cannot:

     1. TIME ASSEMBLY. Three selects, one submitted value. Shopify emails every
        contact[…] key as its own line, so three named selects produce three
        useless lines ("hour: 2", "minute: 30", "ampm: PM") and no time. The
        selects are therefore nameless and the hidden field is assembled here.

     2. TIME GROUP VALIDATION. Marking all three `required` gives three separate
        native bubbles pointing into a flex row, which reads as three broken
        fields rather than one incomplete one. So the group is validated as a
        unit: one message, aria-invalid on the group, focus to the first empty
        select.

     3. CLOSED WEEKDAYS. A native date input cannot disable individual weekdays.
        This is the only place the check can live — and it is a check after the
        fact, which is why the setting is documented as a hint, not a lock.

     4. HUMAN-READABLE COPIES. yyyy-mm-dd in a notification email is a real
        mis-booking risk once a human re-keys "03/04" into a diary, so a readable
        copy of the date rides along. Same idea for the combined full name, which
        keeps a merchant's existing notification template working.

     5. EMBED SURVIVAL. Script nodes written through Liquid into the embed slot
        never execute, and almost every booking widget binds on DOMContentLoaded.
        Both are handled below.

   NOT HERE, ON PURPOSE

     · No mailto submission. See the section's header comment.
     · No mode switching. Only the selected mode's markup is ever in the DOM.
     · No reveal-on-scroll, no scroll lock, no tab controller, no category bus —
       nothing in window.KitInteractions is relevant, so nothing is called.
     · No document-level singletons, no getElementById. Everything is scoped to
       the section root. The prototype's #bookForm / #formStatus break the moment
       a second instance exists, which in the theme editor is one click away.

   THEME-EDITOR LIFECYCLE. shopify:section:load re-inits (the node is new),
   shopify:section:unload tears the instance down so listeners and timers do not
   leak across editor re-renders.
   ========================================================================== */

(function () {
  'use strict';

  var ROOT_SELECTOR = '[data-booking-form]';
  var instances = new WeakMap();

  /* Module-level, deliberately NOT per-instance: it exists to stop the same
     provider bundle being fetched and evaluated twice when the section is
     re-rendered or present twice on one page. Most widget bundles register a
     global and misbehave badly when evaluated a second time. */
  var injectedScriptSrcs = Object.create(null);

  function toArray(list) {
    return Array.prototype.slice.call(list || []);
  }

  function pad2(value) {
    return value < 10 ? '0' + value : '' + value;
  }

  function parseIntOr(raw, fallback) {
    var parsed = parseInt(raw, 10);
    return isNaN(parsed) ? fallback : parsed;
  }

  /* ---------------------------------------------------------------- instance */

  function BookingForm(root) {
    this.root = root;
    this.mode = root.getAttribute('data-form-mode') || 'booking';
    this.form = root.querySelector('form');
    this.bound = [];
    this.timers = [];

    if (this.mode === 'embed') {
      this.initEmbed();
      return;
    }
    if (!this.form) return;

    this.collect();
    this.bindAll();

    /* Initial sync so a browser-restored form (back button, autofill) already
       has correct hidden values before anyone touches a control. */
    this.syncName();
    this.syncDate(false);
    this.syncTime(false);
  }

  BookingForm.prototype.collect = function () {
    var root = this.root;

    this.firstName = root.querySelector('[data-booking-first-name]');
    this.lastName = root.querySelector('[data-booking-last-name]');
    this.fullName = root.querySelector('[data-booking-full-name]');

    this.dateInput = root.querySelector('[data-booking-date]');
    this.dateWords = root.querySelector('[data-booking-date-words]');
    this.dateError = root.querySelector('[data-booking-date-error]');
    this.closedDays = [];
    this.closedMessage = '';
    if (this.dateInput) {
      this.closedMessage = this.dateInput.getAttribute('data-closed-message') || '';
      (this.dateInput.getAttribute('data-closed-weekdays') || '')
        .split(',')
        .forEach(
          function (raw) {
            var day = parseInt(raw, 10);
            if (!isNaN(day) && day >= 0 && day <= 6) this.closedDays.push(day);
          }.bind(this)
        );
    }

    this.timeGroup = root.querySelector('[data-booking-time-group]');
    this.timeValue = root.querySelector('[data-booking-time-value]');
    this.timeError = root.querySelector('[data-booking-time-error]');
    if (this.timeGroup) {
      this.timeHour = this.timeGroup.querySelector('[data-booking-time="hour"]');
      this.timeMinute = this.timeGroup.querySelector('[data-booking-time="minute"]');
      this.timeMeridiem = this.timeGroup.querySelector('[data-booking-time="meridiem"]');
      this.timeIs24h = this.timeGroup.getAttribute('data-format') === '24h';
      this.timeRequired = this.timeGroup.getAttribute('data-required') === 'true';
      this.timeMessage = this.timeGroup.getAttribute('data-error-message') || '';
      this.openHour = parseIntOr(this.timeGroup.getAttribute('data-open-hour'), 0);
      this.closeHour = parseIntOr(this.timeGroup.getAttribute('data-close-hour'), 23);
    }

    this.locationSelect = root.querySelector('[data-booking-location]');
  };

  BookingForm.prototype.on = function (target, type, handler) {
    if (!target) return;
    target.addEventListener(type, handler);
    this.bound.push({ target: target, type: type, handler: handler });
  };

  BookingForm.prototype.bindAll = function () {
    var self = this;

    ['input', 'change'].forEach(function (type) {
      self.on(self.firstName, type, function () {
        self.syncName();
      });
      self.on(self.lastName, type, function () {
        self.syncName();
      });
    });

    self.on(self.dateInput, 'change', function () {
      self.syncDate(true);
    });

    [self.timeHour, self.timeMinute, self.timeMeridiem].forEach(function (select) {
      self.on(select, 'change', function () {
        self.syncTime(true);
      });
    });

    self.on(self.form, 'submit', function (event) {
      self.onSubmit(event);
    });
  };

  /* ------------------------------------------------------------------ name */

  BookingForm.prototype.syncName = function () {
    if (!this.fullName) return;
    var first = this.firstName ? this.firstName.value.trim() : '';
    var last = this.lastName ? this.lastName.value.trim() : '';
    var combined = (first + ' ' + last).replace(/\s+/g, ' ').trim();
    this.fullName.value = combined;
    /* A disabled control is not submitted. Without this the legacy combined-name
       field would add an empty line to every notification email whose form was
       submitted before either name box was filled. */
    this.fullName.disabled = combined === '';
  };

  /* ------------------------------------------------------------------ date */

  BookingForm.prototype.syncDate = function (announce) {
    var input = this.dateInput;
    if (!input) return true;

    this.setMessage(this.dateError, '');
    if (input.setCustomValidity) input.setCustomValidity('');
    input.removeAttribute('aria-invalid');

    var raw = input.value;
    if (this.dateWords) this.dateWords.value = '';
    if (!raw) return true;

    /* Split by hand. `new Date('2026-08-14')` is parsed as UTC midnight, so in
       any timezone behind UTC it reports the PREVIOUS day — which would reject
       Mondays as closed Sundays for half the world. */
    var parts = raw.split('-');
    if (parts.length !== 3) return true;
    var picked = new Date(parseIntOr(parts[0], 1970), parseIntOr(parts[1], 1) - 1, parseIntOr(parts[2], 1));
    if (isNaN(picked.getTime())) return true;

    if (this.dateWords) this.dateWords.value = this.formatDate(picked);

    if (this.closedDays.indexOf(picked.getDay()) !== -1) {
      /* Same rule as the time group: on the initial sync (announce === false) the
         value is reported back but nothing is flagged, so a browser-restored form
         does not open with an error the visitor has not caused yet. */
      if (announce !== false) {
        this.setMessage(this.dateError, this.closedMessage);
        input.setAttribute('aria-invalid', 'true');
      }
      return announce === false;
    }
    return true;
  };

  BookingForm.prototype.formatDate = function (date) {
    var locale = document.documentElement.getAttribute('lang') || undefined;
    try {
      return date.toLocaleDateString(locale, {
        weekday: 'short',
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      });
    } catch (error) {
      /* Intl options are widely supported but not guaranteed; a readable
         fallback beats an empty field in the merchant's email. */
      return date.toDateString();
    }
  };

  /* ------------------------------------------------------------------ time */

  /* Returns null when incomplete, otherwise { hour24, minute, label }. */
  BookingForm.prototype.readTime = function () {
    if (!this.timeHour || !this.timeMinute) return null;
    var hourRaw = this.timeHour.value;
    var minuteRaw = this.timeMinute.value;
    if (!hourRaw || !minuteRaw) return null;

    var hour24 = parseIntOr(hourRaw, -1);
    var minute = parseIntOr(minuteRaw, -1);
    if (hour24 < 0 || minute < 0) return null;

    var label;
    if (this.timeIs24h) {
      label = pad2(hour24) + ':' + pad2(minute);
    } else {
      var meridiem = this.timeMeridiem ? this.timeMeridiem.value : 'AM';
      hour24 = hour24 % 12;
      if (meridiem === 'PM') hour24 += 12;
      label = hourRaw + ':' + pad2(minute) + ' ' + meridiem;
    }
    return { hour24: hour24, minute: minute, label: label };
  };

  BookingForm.prototype.syncTime = function (announce) {
    if (!this.timeGroup || !this.timeValue) return true;

    var parsed = this.readTime();
    this.timeValue.value = parsed ? parsed.label : '';

    var valid = true;
    if (!parsed) {
      valid = !this.timeRequired;
    } else if (parsed.hour24 < this.openHour || parsed.hour24 > this.closeHour) {
      /* Only reachable in 12-hour mode: the hour list is the union of both
         meridiems, so "1 AM" is selectable even when the salon opens at 9. */
      valid = false;
    }

    if (valid || announce === false) {
      /* announce === false is the initial sync. An incomplete-but-required trio
         is the NORMAL state of a form nobody has filled in yet, so flagging it
         aria-invalid on page load would announce a broken field to a screen
         reader before the visitor has done anything. State is only asserted once
         the visitor has actually interacted or submitted. */
      this.timeGroup.removeAttribute('aria-invalid');
      this.setMessage(this.timeError, '');
    } else {
      this.timeGroup.setAttribute('aria-invalid', 'true');
      this.setMessage(this.timeError, this.timeMessage);
    }
    return valid;
  };

  BookingForm.prototype.focusFirstEmptyTime = function () {
    var candidates = [this.timeHour, this.timeMinute, this.timeMeridiem];
    for (var i = 0; i < candidates.length; i++) {
      if (candidates[i] && !candidates[i].value) {
        candidates[i].focus();
        return;
      }
    }
    if (this.timeHour) this.timeHour.focus();
  };

  /* ---------------------------------------------------------------- submit */

  BookingForm.prototype.onSubmit = function (event) {
    /* Native constraint validation has already passed by the time a submit event
       fires, so everything below is only the checks the platform cannot make. */
    this.syncName();

    var dateOk = this.syncDate(true);
    var timeOk = this.syncTime(true);

    if (!dateOk) {
      event.preventDefault();
      if (this.dateInput) this.dateInput.focus();
      return;
    }
    if (!timeOk) {
      event.preventDefault();
      this.focusFirstEmptyTime();
      return;
    }

    /* The provider-migration seam. A Salon location block carrying a booking
       link takes the visitor straight there instead of posting — which is how
       one location can move to a provider before the others, with no rebuild.
       Kept as one obvious branch precisely because it is the code path a future
       migration will reach for first. */
    var redirect = this.locationRedirect();
    if (redirect) {
      event.preventDefault();
      window.location.assign(redirect);
    }
  };

  BookingForm.prototype.locationRedirect = function () {
    if (!this.locationSelect) return null;
    var option = this.locationSelect.options[this.locationSelect.selectedIndex];
    if (!option) return null;
    var url = option.getAttribute('data-booking-url');
    return url ? url : null;
  };

  /* --------------------------------------------------------------- messages */

  BookingForm.prototype.setMessage = function (node, text) {
    if (!node) return;
    var value = text || '';
    /* Only write when it changes: an aria-live region re-announces on every
       mutation, and re-writing the same string on each keystroke makes a screen
       reader repeat itself indefinitely. */
    if (node.textContent === value) return;
    node.textContent = value;
  };

  /* ------------------------------------------------------------------ embed */

  BookingForm.prototype.initEmbed = function () {
    var slot = this.root.querySelector('[data-booking-embed-slot]');
    if (!slot) return;
    this.embedSlot = slot;

    /* Scripts that arrive as parsed HTML from a Liquid setting are inert — the
       HTML parser only executes a <script> it encounters during document parse,
       and a section re-rendered by the theme editor is inserted, not parsed. So
       each one is replaced with a fresh, freshly-created node. */
    toArray(slot.querySelectorAll('script')).forEach(function (original) {
      if (original.hasAttribute('data-booking-executed')) return;
      original.setAttribute('data-booking-executed', '');

      var src = original.getAttribute('src');
      if (src) {
        var resolved = original.src || src;
        if (injectedScriptSrcs[resolved]) return;
        injectedScriptSrcs[resolved] = true;
      }

      var fresh = document.createElement('script');
      toArray(original.attributes).forEach(function (attribute) {
        if (attribute.name === 'data-booking-executed') return;
        fresh.setAttribute(attribute.name, attribute.value);
      });
      fresh.text = original.text;
      original.parentNode.replaceChild(fresh, original);
    });

    this.watchEmbed();
  };

  /* A cross-origin widget can fail for reasons no <noscript> detects: a blocked
     third-party frame, an extension, a provider outage. If nothing has rendered
     after a generous grace period, the always-present fallback link is promoted
     rather than left looking decorative. */
  BookingForm.prototype.watchEmbed = function () {
    var fallback = this.root.querySelector('[data-booking-embed-fallback]');
    var slot = this.embedSlot;
    if (!fallback || !slot) return;

    var timer = window.setTimeout(function () {
      var rendered = toArray(slot.children).some(function (child) {
        return child.tagName !== 'SCRIPT';
      });
      fallback.classList.toggle('booking-form__embed-fallback--promoted', !rendered);
    }, 6000);
    this.timers.push(timer);
  };

  /* ---------------------------------------------------------------- teardown */

  BookingForm.prototype.destroy = function () {
    this.bound.forEach(function (entry) {
      entry.target.removeEventListener(entry.type, entry.handler);
    });
    this.bound = [];
    this.timers.forEach(window.clearTimeout);
    this.timers = [];
  };

  /* --------------------------------------------------------------- lifecycle */

  function init(scope) {
    var context = scope && scope.querySelectorAll ? scope : document;
    var roots = toArray(context.querySelectorAll(ROOT_SELECTOR));
    if (context.matches && context.matches(ROOT_SELECTOR)) roots.unshift(context);

    roots.forEach(function (root) {
      if (instances.has(root)) return;
      instances.set(root, new BookingForm(root));
    });
  }

  function teardown(scope) {
    if (!scope || !scope.querySelectorAll) return;
    var roots = toArray(scope.querySelectorAll(ROOT_SELECTOR));
    if (scope.matches && scope.matches(ROOT_SELECTOR)) roots.unshift(scope);

    roots.forEach(function (root) {
      var instance = instances.get(root);
      if (!instance) return;
      instance.destroy();
      instances.delete(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init(document);
    });
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    teardown(event.target);
  });
})();
